import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, ErrorBox } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";

export function AuthFrame({ children, title, subtitle }) {
  const { lang, setLang } = useI18n();
  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex flex-col justify-between bg-[#153A63] p-12 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="relative">
          <img data-testid="login-logo" src="/motravay-logo-white.png" alt="Motravay" className="h-9 w-auto" />
          <div className="text-xs uppercase tracking-[0.25em] text-sky-200 mt-2">HR Audit 360°</div>
        </div>
        <div className="relative max-w-md">
          <p className="font-display text-3xl font-semibold leading-snug">Témoignage, observation, preuve vérifiée, constat validé.</p>
          <p className="text-sky-100/80 mt-4 text-sm">Espace de mission interne — accès réservé aux collaborateurs et consultants Motravay autorisés.</p>
        </div>
        <div className="relative text-xs text-sky-200/70">Motravay Web Services Ltd · Mauritius</div>
      </div>
      <div className="flex flex-col p-6 sm:p-12 bg-white">
        <div className="flex justify-end gap-1 text-xs font-semibold">
          {["fr", "en"].map((l) => (
            <button key={l} data-testid={`login-lang-${l}`} onClick={() => setLang(l)} className={`px-2 py-1 rounded ${lang === l ? "bg-[#153A63] text-white" : "text-slate-600"}`}>{l.toUpperCase()}</button>
          ))}
        </div>
        <div className="my-auto w-full max-w-sm mx-auto fade-up">
          <div className="lg:hidden mb-6 flex items-center gap-3"><img data-testid="login-logo-mobile" src="/motravay-logo.png" alt="Motravay" className="h-7 w-auto" /><span className="text-sm font-medium text-[#2176AE]">HR Audit 360°</span></div>
          <h1 className="font-display text-2xl font-bold text-[#153A63]">{title}</h1>
          {subtitle && <p className="text-sm text-slate-600 mt-1">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { user, setUser } = useAuth();
  const { tx } = useI18n();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfaToken, setMfaToken] = useState(null);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  if (user) return <Navigate to="/" replace />;

  const run = async (fn) => {
    setErr(""); setBusy(true);
    try { await fn(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setInfo(tx("Si le compte existe, un lien de réinitialisation a été généré (transmis par l'administrateur).", "If the account exists, a reset link was generated (delivered by your administrator)."));
        return;
      }
      const { data } = mfaToken ? await api.post("/auth/login/mfa", { mfa_token: mfaToken, code }) : await api.post("/auth/login", { email, password });
      if (data.mfa_required) { setMfaToken(data.mfa_token); return; }
      setUser(data.user);
      nav("/");
    });
  };

  return (
    <AuthFrame title={mode === "forgot" ? tx("Mot de passe oublié", "Forgot password") : mfaToken ? tx("Vérification MFA", "MFA verification") : tx("Connexion", "Sign in")}
      subtitle={mfaToken ? tx("Saisissez le code à 6 chiffres de votre application d'authentification.", "Enter the 6-digit code from your authenticator app.") : tx("Aucune inscription publique. Comptes sur invitation uniquement.", "No public sign-up. Invitation-only accounts.")}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {err && <ErrorBox error={err} />}
        {info && <div data-testid="login-info" className="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">{info}</div>}
        {!mfaToken && (
          <Field label={tx("E-mail professionnel", "Work email")}>
            <Input data-testid="login-email-input" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
        )}
        {!mfaToken && mode === "login" && (
          <Field label={tx("Mot de passe", "Password")}>
            <Input data-testid="login-password-input" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
        )}
        {mfaToken && (
          <Field label={tx("Code MFA", "MFA code")}>
            <Input data-testid="login-mfa-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value)} className="tracking-[0.4em] font-mono text-lg" />
          </Field>
        )}
        <Button type="submit" data-testid="login-submit-button" disabled={busy} className="w-full bg-[#153A63] hover:bg-[#1D6494]">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4 mr-1" />}
          {mode === "forgot" ? tx("Demander un lien", "Request link") : mfaToken ? tx("Vérifier", "Verify") : tx("Se connecter", "Sign in")}
        </Button>
        <button type="button" data-testid="login-toggle-forgot" className="text-sm text-[#2176AE] hover:underline" onClick={() => { setMode(mode === "login" ? "forgot" : "login"); setInfo(""); setErr(""); setMfaToken(null); }}>
          {mode === "login" ? tx("Mot de passe oublié ?", "Forgot password?") : tx("Retour à la connexion", "Back to sign in")}
        </button>
      </form>
    </AuthFrame>
  );
}
